// HomePage static data + types, extracted from HomePage.tsx (MEV-539).
// Pure data (metrics, steps, feature catalogue) — no behavior. Icons are
// lucide component refs held as values.
import { type ElementType } from 'react'
import {
  Zap, Eye, ShieldAlert, ListChecks, Server,
} from 'lucide-react'

// W7: step 02 used to say the engine "scans every swap for sandwich attacks,
// frontrunning, and JIT liquidity" and step 03 "Transactions route through MEV
// Shield — full protection, zero extra gas". Detection is on demand, sandwich
// only (mev_detect.py NOT_COVERED), Ethereum by default (chain_client.py
// DEFAULT_CHAINS); POST /api/protect/transaction answers 501.
export const HOW_IT_WORKS = [
  { step: '01', title: 'Connect Wallet', desc: 'Link your MetaMask or any EVM wallet via EIP-4361 sign-in.' },
  { step: '02', title: 'Check a Transaction', desc: 'Signed-in users can query GET /api/mev/detect/{txHash} to see whether a mined Ethereum transaction was sandwiched. There is no dashboard form for it yet.' },
  { step: '03', title: 'Protected Execution', desc: 'Not available yet: MEV Shield does not route or protect transactions today.' },
]


export interface HomeFeature {
  icon: ElementType
  title: string
  desc: string
  tag: string
  blogSlug?: string
  learn: { overview: string; howItWorks: string[]; whyItMatters: string; techDetails: string[] }
}

export const FEATURES: HomeFeature[] = [
  // W7: this card claimed "Sub-100ms MEV attack detection via gRPC streaming —
  // sandwich, frontrun, JIT, and arbitrage" from mempool scanning, with
  // protection intercepting the attack. None of that is implemented. What runs
  // is GET /api/mev/detect/{txHash} (routers/dashboards.py:122, mev_detect.py).
  {
    icon: Zap,
    title: 'Sandwich Detection',
    desc: 'Query the authenticated API with a mined Ethereum transaction hash and see whether it was the victim or attacker in a sandwich, with the block, pool and attacker transactions as evidence. There is no dashboard form for it yet.',
    tag: 'Detection',
    learn: {
      overview: 'MEV Shield applies MEV-Inspect\'s sandwich rule to the Uniswap V2 and V3 swap logs of the transaction\'s block. The answer is detected, not detected, or unknown with a reason. It runs on demand for a transaction that is already mined; it does not watch the mempool and does not stop an attack.',
      howItWorks: [
        'A signed-in client calls GET /api/mev/detect/{txHash} (there is no dashboard form yet). MEV Shield reads the receipt and the swap logs of that block from Etherscan.',
        'Swaps in the same pool are ordered by position in the block. A front swap, one or more victim swaps in the same direction, and a closing swap by the same sandwicher make a sandwich.',
        'The verdict names the block, the pool and the attacker transactions. When no answer is possible (unsupported chain, pending transaction, provider unavailable) the verdict is unknown, never "not detected".',
      ],
      whyItMatters: 'Knowing whether a past swap was sandwiched tells you whether the price you got was manipulated. Front-running, back-running, JIT liquidity and arbitrage are not covered yet.',
      techDetails: ['GET /api/mev/detect/{txHash}', 'MEV-Inspect sandwich rule', 'Uniswap V2 + V3 swap logs', 'Ethereum (other chains not enabled)', 'Tri-state verdict: detected / not detected / unknown'],
    },
  },
  {
    icon: Eye,
    title: 'Institutional Intelligence',
    desc: 'Large transfers from an Alchemy webhook stream, five known institutional wallets on Etherscan and Aave events, with ETH/BTC pricing.',
    tag: 'Intelligence',
    learn: {
      overview: 'Large on-chain flows from three sources, merged into one list. Dollar figures use ETH and BTC prices; flows in other assets are reported as unpriced.',
      howItWorks: [
        'Sources (backend-enterprise/services/v2): an Alchemy webhook stream is the primary source; Etherscan polling of five known institutional wallets (Coinbase Prime, Binance Cold Wallet, Kraken, Grayscale GBTC, Fireblocks Custody) keeps transfers of at least 500 ETH; Aave events are read from a Redis stream. Dune is no longer used (its endpoint returned stale cached results).',
        'USD values use ETH and BTC prices only (ETH: Etherscan, then CoinGecko, then a configured fallback; BTC: CoinGecko, then a configured fallback). On the homepage panel (/api/mev-stats) and in the Whale Flows dashboard (/api/whale-flows), a flow in another asset, or one with no usable quote, is reported as unpriced instead of being given a price. The API\'s `priceSource` field, shown as one ETH and one BTC badge in the Whale Flows view, says, for each of those two prices, whether it was fetched live (from Etherscan or CoinGecko), served from the cache or taken from the configured fallback, or that no quote was available.',
        'Results are cached in Redis for 60 seconds; `/api/mev-stats?refresh=1` bypasses the cache. Etherscan rows show their age from the block time; rows from the Alchemy and Aave streams show their age from the time MEV Shield received them. Age labels are worked out when the list is built, so a cached list can be up to 60 seconds behind. Older stream rows stored without a receive time are labelled "unknown age".',
        'The Whale Flows list is deduplicated by transaction hash. Each row carries a rule-based BULLISH, BEARISH, ACCUMULATION or NEUTRAL signal from its direction and size, and expanding a row asks the AI assistant (/api/chat) to explain it.',
        'When no source returns usable rows, the endpoints return empty lists instead of inventing institutions.',
      ],
      whyItMatters: 'Large institutional wallet movements are public but scattered across sources. MEV Shield merges them into one list.',
      techDetails: ['Alchemy webhook stream', 'Etherscan API v2 (5 known wallets, ≥500 ETH)', 'Aave events (Redis stream)', 'ETH/BTC price resolver + priceSource', 'Redis 60s TTL · `?refresh=1` bypass', 'txHash deduplication', 'Rule-based flow signals', 'AI explanation via /api/chat'],
    },
  },
  // W7: this card claimed validation "against known poisoning databases",
  // lookalike matching before you sign, HMAC hashing and blocking. Only the
  // EIP-55 checksum check is routed (main.py:177). The similarity scorer in
  // address_poisoning.py:89-139 exists but no route exposes it.
  {
    icon: ShieldAlert,
    title: 'Address Checksum Check',
    desc: 'Check an Ethereum address against its EIP-55 checksum. A mismatch means the address was mistyped or altered. Lookalike-address detection is not available yet.',
    tag: 'Security',
    blogSlug: 'address-poisoning-protection',
    learn: {
      overview: 'Address poisoning attacks trick users into sending funds to lookalike addresses that match the first and last characters of one they trust. MEV Shield can check an address\'s EIP-55 checksum today. Comparing an address against your transaction history to catch lookalikes is not available yet.',
      howItWorks: [
        'You paste an address on the dashboard\'s Address Check tab, which calls POST /v2/addresses/validate.',
        'The API recomputes the EIP-55 checksum (Keccak-256 of the lowercase address) and compares it with the capitalisation you supplied.',
        'A mismatch is reported; it may mean the address was mistyped or altered. A valid checksum does not prove the address is safe: a lookalike address has a valid checksum of its own.',
      ],
      whyItMatters: 'A checksum mismatch catches a mistyped or altered address before you use it. It does not catch a lookalike address, which is why lookalike comparison is the next step.',
      techDetails: ['POST /v2/addresses/validate', 'EIP-55 checksum', 'Keccak-256', 'Lookalike comparison: not available yet'],
    },
  },
  // W6 phase 3: the MEV Insurance feature card was removed. Insurance is not a
  // live product (tiers, premiums and claims were described as if it were).
  {
    icon: ListChecks,
    title: 'Agentic protection layer',
    desc: 'Policy-guarded tool execution — Guardian analyzer, hierarchical kill-switch, Admin approvals inbox, Edge Defender tiers, KPI telemetry hooks, deterministic audit chaining, SIWE-aligned offline replay.',
    tag: 'Agentic',
    learn: {
      overview: '`services/v2/app/agentic_ai/` (the Python port of the Node `services/agenticAi/`) delivers the guarded automation substrate that powers approvals, auditability, KPI exports, deterministic edge triage, and capability-aware device bridges without bypassing organisational controls.',
      howItWorks: [
        '`toolSchema` + `policyEngine` enumerate callable tools with forbid / requireApproval / autoApprove ladders while `guardian.analyze()` reasons over intents before dispatch.',
        '`actionExecutor` consults cascading kill-switches (`global → org → user`) and records each hop via chained HMAC auditing (`auditTrail.js`).',
        'Admins adjudicate queued decisions through SPA cards backed by REST (`/api/agentic/approvals/...`).',
        '`edgeDefender` pairs millisecond classifier triage with selective deep reasoning budgets; `onDeviceBridge` replays buffered calls with cryptographic snapshot signing tied to OrgId.',
      ],
      whyItMatters: 'Automation without instrumentation is liability. Baking Guardian reasoning, deterministic parser repair, KPI subscribers, approvals, SIWE-aligned offline buffers, plus kill-switch depth into the API means bots inherit the exact controls humans negotiated — SOC teams keep receipts.',
      techDetails: ['Tool schema · constrained parser (`constrainedParser.js`)', '`GET/POST /api/agentic/*` approvals', 'Tiered kill-switch (`killSwitch` cascade)', '`kpiTelemetry` Prometheus hooks', '`edgeDefender` hot/cold path', '`onDeviceBridge` signed FIFO replay', '`auditTrail` HMAC chaining'],
    },
  },
  {
    icon: Server,
    title: 'Platform observability',
    desc: 'Eight-service Compose blueprint (Express gateway, SPA, FastAPI sidecar v2, PostgreSQL 16, Redis 7, dual NGINX, Prometheus scrape, Grafana) with Postgres/Redis‑aware `/health`.',
    tag: 'Operations',
    learn: {
      overview: 'Production deploy artefacts (`deploy.yaml`) pin an eight-service mesh so SPA, REST, auxiliary Python workloads, datastore, caches, reverse proxies, and observability exporters stay version-locked.',
      howItWorks: [
        '`GET /health` authenticates Postgres + Redis connectivity alongside process status so uptime monitors flare before customers notice regressions.',
        'Dual NGINX layers enforce layered CSP intersections exactly as INS-089 documents.',
        'Prometheus scrapers ingest agentic KPI events while Grafana boards visualise SLA burn-down for compliance reviews.',
        'Operational scripts (`deploy-to-app-mevshield.sh`) hydrate the Compose stack verbatim — parity between laptops and prod.',
      ],
      whyItMatters: 'Operational drift is where incidents hide. Packaging health semantics with metrics exporters gives SecOps repeatable evidence instead of improvised curl checks.',
      techDetails: ['8 Compose services', '`GET /health` DB / Redis probing', 'Prometheus + Grafana pairing', 'PostHog & Pinecone optional hooks', 'INS-089 CSP symmetry'],
    },
  },
]

// W7: this list used to include dedicated RPC endpoints, on-chain audit logs,
// multi-chain support, webhook integrations, RBAC, liquidation auto-shield,
// bridge MEV coverage, MiCA/SEC/FCA/MAS/ASIC reporting, 7-year retention, the
// AI/Pinecone intelligence platform and custom compliance cadence. None is a
// live feature. Only what the API serves today remains.
export const ENTERPRISE_FEATURES = [
  'Sandwich detection API by transaction hash (Ethereum)',
  'EIP-55 address checksum API',
  'Institutional whale-flow tracking',
  'Agentic approvals queue (admin)',
  'Liquidity registry + LP analytics dashboards',
]

// Per-chain cards on the home page: presentation only. W6: the cards used to
// start from invented totals (e.g. 1.248B USD "saved" on Ethereum) and tick
// upward with Math.random. They now show /api/stats counts via statsKey.
export const CHAIN_BASE: {
  id: string; name: string; symbol: string; color: string; borderColor: string;
  bgColor: string; dotColor: string; gradientFrom: string;
  /** key under /api/stats `chains`, or null when MEV Shield records nothing for this chain */
  statsKey: string | null
}[] = [
  {
    id: 'eth', name: 'Ethereum', symbol: 'ETH',
    color: 'text-blue-400', borderColor: 'border-blue-500/20', bgColor: 'bg-blue-500/10',
    dotColor: 'bg-blue-400', gradientFrom: 'from-blue-500/20', statsKey: 'ethereum',
  },
  {
    // /chain/:id is keyed "polygon" (ChainDetailPage); "matic" was a dead link.
    id: 'polygon', name: 'Polygon', symbol: 'MATIC',
    color: 'text-purple-400', borderColor: 'border-purple-500/20', bgColor: 'bg-purple-500/10',
    dotColor: 'bg-purple-400', gradientFrom: 'from-purple-500/20', statsKey: 'polygon',
  },
  {
    id: 'sol', name: 'Solana', symbol: 'SOL',
    color: 'text-cyan-400', borderColor: 'border-cyan-500/20', bgColor: 'bg-cyan-500/10',
    dotColor: 'bg-cyan-400', gradientFrom: 'from-cyan-500/20', statsKey: null,
  },
]
